<template>
  <div class="flex flex-col">
    <!--
  Why fields are outstanding, in the assistant's own words when it read the
  sentence at all. Stated before the records, because it explains why half
  of them are empty.
-->
    <DSAlert
      v-if="note"
      tone="brand"
      live="polite"
      class="mb-4"
    >
      <template #icon>
        <Sparkles />
      </template>
      <DSAlertDescription class="text-sm">
        {{ note }}
      </DSAlertDescription>
    </DSAlert>
    <!--
      The account, once, across the top. It is not a field of the property or of
      the loan — both records post to it — so stating it on either card made the
      one account look like two, and burying it among eighteen cells made the
      frame look like a detail. Read-only, and the way back to the step that
      sets it. Only for a wizard that asks the question at all.
    -->
    <SummaryAccount
      v-if="ownershipStep"
      class="mb-4"
      :name="account.name"
      :type="account.type"
      :is-new="account.isNew"
      :owed="account.owed"
      :origin="account.origin"
      :step-label="ownershipStep.label"
      @edit="emit('edit-account')"
    />

    <!--
      A fund split across more than one investor has no single account to
      state the way SummaryAccount does — each row is its own bundle, so the
      block lists them instead of naming one.
    -->
    <div
      v-if="investorsStep"
      class="flex flex-col gap-2.5 p-4 mb-4 bg-background border rounded-ds-lg"
      :class="investorsOwed ? 'border-warning' : 'border-border'"
    >
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <DSFieldLabel
          label="Investing entities"
          :mark="investorsOwed ? 'required' : undefined"
        />
        <DSButton
          type="button"
          variant="link"
          size="sm"
          class="shrink-0 h-auto px-0 font-bold justify-start"
          @click="emit('edit-investors')"
        >
          Set in {{ investorsStep.label }}
          <ArrowRight class="size-3" />
        </DSButton>
      </div>

      <InvestorAllocationBar
        :investors
        :commitment="values.amount"
        :currency="values.currency"
      />
    </div>

    <!--
      private_fund's own entities: no split to show — each row independently
      holds the full amount — so the block lists them rather than drawing the
      allocation bar the split flow's own block above does.
    -->
    <div
      v-if="entitiesStep"
      class="flex flex-col gap-2.5 p-4 mb-4 bg-background border rounded-ds-lg"
      :class="entitiesOwed ? 'border-warning' : 'border-border'"
    >
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <DSFieldLabel
          label="Investing entities"
          :mark="entitiesOwed ? 'required' : undefined"
        />
        <DSButton
          type="button"
          variant="link"
          size="sm"
          class="shrink-0 h-auto px-0 font-bold justify-start"
          @click="emit('edit-entities')"
        >
          Set in {{ entitiesStep.label }}
          <ArrowRight class="size-3" />
        </DSButton>
      </div>

      <ul class="flex flex-col gap-1.5 text-sm font-medium">
        <li
          v-for="(entity, index) in investors"
          :key="index"
          class="flex items-center gap-1.5"
        >
          <span>{{ entity.accountName || `Investor ${index + 1}` }}</span>
          <span
            v-if="entity.accountType"
            class="text-xs font-normal text-muted-foreground"
          >
            · {{ entity.accountType }}
          </span>
        </li>
      </ul>
    </div>

    <!--
      The records themselves. The account they hang off is stated once above
      rather than on each card: it is still the ownership step's question, and
      answering it twice on the same screen is what made the owner look like
      something each record creates.
    -->
    <template
      v-for="(record, index) in definition.records"
      :key="record.key"
    >
      <RecordLink
        v-if="record.link && index"
        :label="record.link"
      />

      <RecordCard
        :icon="record.icon"
        :tone="record.tone"
        :transaction-type="record.transactionType"
      >
        <template #heading>
          <WizardField
            v-if="headingField(record)"
            :form
            :field="headingField(record)!"
            :origin="origins[record.heading]"
            :readonly="readonlyFields.includes(record.heading)"
          />
        </template>

        <template #fields>
          <WizardFields
            :form
            :fields="recordFields(record)"
            :origins
            :readonly-fields
            :columns="3"
          />
        </template>
      </RecordCard>
    </template>

    <!--
      What the asset is worth, between the records and the tags: it is a fact
      about the asset the records create rather than one of the records, and
      the same is true of the tags below it.
    -->
    <SummaryValues
      v-if="valueStep && marks.length"
      class="mt-4"
      :marks
      :currency="values.currency"
      :step-label="valueStep.label"
      @edit="emit('edit-value')"
    />

    <!--
      Capital calls, fees and distributions, read back the same way marks
      are: a fact the fund's records carry rather than a record of its own —
      see SummaryValues just above.
    -->
    <SummaryCashFlows
      v-if="cashFlowsStep && cashFlows.length"
      class="mt-4"
      :cash-flows="cashFlows"
      :investors="cashFlowInvestors"
      :currency="values.currency"
      :step-label="cashFlowsStep.label"
      @edit="emit('edit-cash-flows')"
    />

    <TagChips
      class="mt-4"
      :selected="tags"
      :suggestions="tagSuggestions"
      :note="tagNote"
      :loading="tagsLoading"
      :askable="tagsAskable"
      removable
      @accept="emit('accept-tag', $event)"
      @remove="emit('remove-tag', $event)"
      @add="emit('add-tag')"
      @ask="emit('ask-tags')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { ArrowRight, Sparkles } from '@lucide/vue'

import { TagRef } from '@/entities/tags/utils/types'

import TagChips from '@/entities/tags/components/TagChips.vue'

import InvestorAllocationBar from './InvestorAllocationBar.vue'
import RecordCard from './RecordCard.vue'
import RecordLink from './RecordLink.vue'
import SummaryAccount from './SummaryAccount.vue'
import SummaryCashFlows from './SummaryCashFlows.vue'
import SummaryValues from './SummaryValues.vue'
import WizardField from './WizardField.vue'
import WizardFields from './WizardFields.vue'

import { ACCOUNT_MODE_FIELD, CASH_FLOWS_FIELD, MARKS_FIELD } from '../utils/const'
import {
  cashFlowsStepOf,
  entitiesOutstandingCount,
  entitiesStepOf,
  fieldByName,
  investorsOutstandingCount,
  investorsStepOf,
  outstandingFields,
  ownershipStepOf,
  valueStepOf,
  visibleFields,
} from '../utils/helpers'
import {
  WizardAccountMode,
  WizardCashFlow,
  WizardDefinition,
  WizardInvestor,
  WizardMark,
  WizardFieldOrigin,
  WizardRecordDef,
} from '../utils/types'
import { DSAlert, DSAlertDescription, DSButton, DSFieldLabel } from '@ds'

/*
 * The last step: the records this wizard will create, as cards.
 *
 * Which fields render as text rather than as controls is the wizard's call —
 * see readonlyFields. Whatever it names is read back here and changed in the
 * step that collects it; the banner above the cards is the way to each field
 * that is still owed.
 */
const { form, definition, origins } = defineProps<{
  form: any
  definition: WizardDefinition
  origins: Record<string, WizardFieldOrigin>
  /** Names to show as text here rather than as controls — see readonlyFields. */
  readonlyFields: string[]
  /** The assistant's summary of what it read, when it was the assistant that opened this. */
  note?: string
  /** Tags applied to the asset so far. */
  tags: TagRef[]
  /** The assistant's tags the user has not accepted yet. */
  tagSuggestions: TagRef[]
  tagNote?: string
  /** The tag assistant is still deciding — drawn as a pending chip. */
  tagsLoading?: boolean
  /** Offers the "Ask AI" chip — see TagChips. */
  tagsAskable?: boolean
}>()

const emit = defineEmits<{
  (e: 'accept-tag', tag: TagRef): void
  (e: 'remove-tag', tag: TagRef): void
  (e: 'add-tag'): void
  /** The "Ask AI" chip was tapped. */
  (e: 'ask-tags'): void
  /** Back to the step that sets the account — the wizard owns which step that is. */
  (e: 'edit-account'): void
  /** Back to the step that splits the commitment across investors. */
  (e: 'edit-investors'): void
  /** Back to private_fund's own step that lists its independent entities. */
  (e: 'edit-entities'): void
  /** Back to the step that records what the asset is worth. */
  (e: 'edit-value'): void
  /** Back to the step that logs capital calls, fees and distributions. */
  (e: 'edit-cash-flows'): void
}>()

const values = form.useSelector((state: any) => state.values)

/*
 * The step that asks the ownership question, when this wizard asks it. A flow
 * that collects a held-in account instead names its step `ownership` too, so
 * this is found by the field rather than by the key — a block stating an
 * account such a flow never collects would state an empty one forever.
 */
const ownershipStep = computed(() => ownershipStepOf(definition))

/*
 * The step that splits a commitment across more than one investing entity —
 * private_fund's own, in place of the ownership step every other flow keeps.
 */
const investorsStep = computed(() => investorsStepOf(definition))

const investors = computed<WizardInvestor[]>(() => values.value.investors ?? [])

/*
 * What the cash-flows block reads a row's investor position against —
 * `investors` itself where a wizard has that step, otherwise the one row the
 * ownership step collected instead, read off the same three fields
 * `ensureOwnerAccount` resolves. Same fallback `CashFlowsStep.vue` draws its
 * own "Investor" picker from, so the summary names the account it actually
 * logged the flow against rather than falling back to "Investor 1".
 */
const cashFlowInvestors = computed<WizardInvestor[]>(() => {
  if (investors.value.length) return investors.value
  if (!values.value.ownerAccountName) return []

  return [{
    accountMode: values.value[ACCOUNT_MODE_FIELD] ?? WizardAccountMode.EXISTING,
    accountId: values.value.ownerAccountId ?? '',
    accountName: values.value.ownerAccountName,
    accountType: values.value.ownerAccountType ?? '',
    share: undefined,
  }]
})

/**
 * Owed by the same count the rail and the Create button already gate on —
 * an unresolved row, a missing share, or a split that does not add up to
 * the commitment. One rule, so the block's amber border can never disagree
 * with what is actually still blocking the create.
 */
const investorsOwed = computed(() =>
  investorsOutstandingCount(investors.value, values.value.amount as number | undefined) > 0)

/*
 * private_fund's own entities step — same slot as `investorsStep` above but
 * never both, since a flow keeps one or the other, never either. Read the
 * same `investors` list: the two steps share it, just with a different
 * shape of row (see WizardInvestor and EntitiesStep.vue).
 */
const entitiesStep = computed(() => entitiesStepOf(definition))

/** Owed by the same count the rail and the Create button already gate on. */
const entitiesOwed = computed(() => entitiesOutstandingCount(investors.value) > 0)

/*
 * The step that records dated values, for a flow that has one. A wizard whose
 * valuation step is still a placeholder records nothing, so there is nothing
 * here to read back.
 */
const valueStep = computed(() => valueStepOf(definition))

/*
 * What it recorded. Nothing recorded draws nothing: the step is optional, and
 * the summary is what the wizard will create — not a list of what it will not.
 */
const marks = computed<WizardMark[]>(() => values.value[MARKS_FIELD] ?? [])

/**
 * The step that logs capital calls, fees and distributions — private_fund's
 * own, same reasoning as `valueStep` just above: a wizard that never built
 * this step has nothing here to read back.
 */
const cashFlowsStep = computed(() => cashFlowsStepOf(definition))

/** What it recorded, same rule as `marks`: nothing logged draws nothing. */
const cashFlows = computed<WizardCashFlow[]>(() => values.value[CASH_FLOWS_FIELD] ?? [])

/*
 * The account as the block reads it back: the same three values either half of
 * the step writes, plus whether anything about it is still owed.
 *
 * `owed` comes from the step's own required predicates rather than from the
 * name being empty — creating an account needs a type as well, and the block
 * has to carry the amber mark for that case too.
 *
 * The sparkle follows the name: an account the assistant resolved is marked on
 * whichever field carries it, and the name is the one the block shows.
 */
const account = computed(() => ({
  name: values.value.ownerAccountName as string | undefined,
  type: values.value.ownerAccountType as string | undefined,
  isNew: values.value[ACCOUNT_MODE_FIELD] === WizardAccountMode.NEW,
  owed: ownershipStep.value
    ? outstandingFields(ownershipStep.value, values.value).length > 0
    : false,
  origin: origins.ownerAccountName,
}))

const headingField = (record: WizardRecordDef) => {
  const field = fieldByName(definition, record.heading)
  if (!field || origins[field.name] === WizardFieldOrigin.FIXED) return undefined
  return field
}

/*
 * A record names its fields; the definitions live with the steps that collect
 * them. A name with no definition is dropped rather than rendered blank — that
 * only happens when a record and its steps disagree, which is a bug in the
 * wizard's own definition, not something to paper over on screen. A value the
 * flow fixes is dropped too: it is not on the card for the same reason it is
 * not in the step.
 */
const recordFields = (record: WizardRecordDef) =>
  visibleFields(
    record.fields
      .map(name => fieldByName(definition, name))
      .filter((field): field is NonNullable<typeof field> => !!field),
    origins,
  )
</script>
